import { useEffect, useRef, useState } from "react"

import { CloseOutlined } from "@ant-design/icons"
import { Flex, Tooltip, Typography } from "antd"
import cls from "classname"
import { CSSTransition, TransitionGroup } from "react-transition-group"

import Button from "../../../../../Components/Button"
import Input from "../../../../../Components/Input"

import styles from "./questionField.module.less"

const { Text } = Typography

const CHIP_TRANSITION_MS = 180
const chipTransitionClassNames = {
  enter: styles.chipEnter,
  enterActive: styles.chipEnterActive,
  exit: styles.chipExit,
  exitActive: styles.chipExitActive
}

// Wrapper that owns its own nodeRef so CSSTransition can drive it without
// findDOMNode (deprecated under React 18 StrictMode). TransitionGroup passes
// its lifecycle props (in, appear, onExited…) through — they're spread onto
// CSSTransition unchanged.
const AnimatedChip = ({ children, ...transitionProps }) => {
  const nodeRef = useRef(null)
  return (
    <CSSTransition
      {...transitionProps}
      nodeRef={nodeRef}
      timeout={CHIP_TRANSITION_MS}
      classNames={chipTransitionClassNames}
    >
      <span ref={nodeRef} className={styles.chipAnimWrap}>
        {children}
      </span>
    </CSSTransition>
  )
}

// Multi-value free-text control (billing descriptors / BIN / CAID): type a
// value, Enter/blur commits it as a removable chip, "+ Add" appends another.
// Stores a string[]; tolerates a legacy single string.
//
// Invalid values still commit — as red "error chips" with a tooltip explaining
// why — so nothing typed is lost as an invisible draft. Field-level validation
// (validateQuestion) re-checks each chip and blocks Next until fixed.
const TextMultiControl = ({
  t,
  values,
  placeholder,
  addLabel,
  onChange,
  validateValue,
  uppercase,
  readOnly,
  estimatedValues = []
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")
  const [dupError, setDupError] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  const commit = () => {
    let v = (draft || "").trim()
    if (!v) {
      setDraft("")
      setDupError(null)
      setEditing(false)
      return
    }
    if (uppercase) v = v.toUpperCase()
    // A duplicate is reported, not committed: two identical chips would be
    // meaningless and impossible to distinguish. User must clear or amend.
    if (values.some((x) => String(x).toLowerCase() === v.toLowerCase())) {
      setDupError("This value has already been added")
      return
    }
    // Invalid values still commit as chips (styled as errors) — see the header
    // comment. Prevents the "typed but nothing saved" trap.
    onChange([...values, v])
    setDraft("")
    setDupError(null)
    setEditing(false)
  }

  // Width follows the longer of what is typed and what is prompted, so an empty
  // input still shows its whole placeholder instead of clipping it mid-word.
  const prompt = placeholder || t("Type and press Enter")
  const inputWidth = Math.max(140, Math.max(draft?.length || 0, prompt.length) * 8 + 32)

  return (
    <>
      <Flex wrap="wrap" gap={8} className={styles.chipRow}>
        <TransitionGroup component={null}>
          {values.map((c, i) => {
            const chipErr = validateValue ? validateValue(String(c)) : null
            // A value suggested from the provider's settings rather than read off a charge (CHA1-4909).
            const isEstimated = !chipErr && estimatedValues.includes(c)
            let chipTooltip = null
            if (chipErr) chipTooltip = t(chipErr)
            if (isEstimated)
              chipTooltip = t("Suggested based on your payment provider data, please verify")
            const chip = (
              <span
                className={cls(
                  styles.chip,
                  styles.chipCustom,
                  chipErr && styles.chipCustomError,
                  isEstimated && styles.chipCustomEstimated
                )}
              >
                <span className={styles.chipCustomText}>{c}</span>
                {readOnly ? null : (
                  <button
                    type="button"
                    className={styles.chipCustomX}
                    aria-label={t("Remove {{item}}", { item: c })}
                    onClick={(e) => {
                      e.stopPropagation()
                      onChange(values.filter((_, idx) => idx !== i))
                    }}
                  >
                    <CloseOutlined />
                  </button>
                )}
              </span>
            )
            // Key by value: commit blocks duplicates, so each chip has a unique
            // identifier stable across reorders — required for TransitionGroup
            // to run the exit animation on the actually-removed item (index
            // keys would just look like content-changed).
            return (
              <AnimatedChip key={c}>
                {chipTooltip ? <Tooltip title={chipTooltip}>{chip}</Tooltip> : chip}
              </AnimatedChip>
            )
          })}
        </TransitionGroup>
        {editing ? (
          <Input
            ref={inputRef}
            value={draft}
            placeholder={prompt}
            onChange={(e) => {
              setDraft(e.target.value)
              if (dupError) setDupError(null)
            }}
            onPressEnter={commit}
            onBlur={commit}
            status={dupError ? "error" : ""}
            className={cls(styles.chip, styles.chipInput)}
            style={{ width: inputWidth }}
            disabled={readOnly}
          />
        ) : (
          <Button
            type="default"
            disabled={readOnly}
            className={readOnly ? {} : cls(styles.chip, styles.chipAdd)}
            onClick={() => setEditing(true)}
            text={values.length ? t("+ Add another") : t(addLabel || "+ Add")}
          />
        )}
      </Flex>
      {dupError ? (
        <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
          {t(dupError)}
        </Text>
      ) : null}
    </>
  )
}

export default TextMultiControl
